import React, { useState } from "react";
import PropTypes from "prop-types";
import { Calendar, Clock } from "lucide-react";
import "./StepTwo.css";

const StepTwo = ({
  onNext,
  onBack,
  currentStep,
  isLastStep,
  initialRaceSettings = {
    raceName: "",
    raceDuration: 1,
    startDate: "",
    startTime: "",
    bettingWindow: { before: 5, after: 5 },
  },
}) => {
  const [raceName, setRaceName] = useState(initialRaceSettings.raceName || "");
  const [raceDuration, setRaceDuration] = useState(
    initialRaceSettings.raceDuration || 1
  ); // hours
  const [startDate, setStartDate] = useState(
    initialRaceSettings.startDate || ""
  );
  const [startTime, setStartTime] = useState(
    initialRaceSettings.startTime || ""
  );
  const [bettingWindow, setBettingWindow] = useState(
    initialRaceSettings.bettingWindow || { before: 5, after: 5 }
  ); // minutes

  const handleRaceNameChange = (e) => {
    setRaceName(e.target.value);
  };

  const handleDurationChange = (e) => {
    const value = parseInt(e.target.value);
    setRaceDuration(value);

    // Calculate percentage for the gradient
    const percentage =
      ((value - e.target.min) / (e.target.max - e.target.min)) * 100;
    e.target.style.setProperty("--value", `${percentage}%`);

    // Update bubble position
    const bubble = e.target.parentElement.querySelector(".value-bubble");
    bubble.style.left = `${percentage}%`;
    bubble.textContent = value;
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleStartTimeChange = (e) => {
    setStartTime(e.target.value);
  };

  const handleBettingWindowChange = (type, e) => {
    const value = parseInt(e.target.value);
    setBettingWindow((prev) => ({
      ...prev,
      [type]: value,
    }));

    // Calculate percentage for the gradient
    const percentage =
      ((value - e.target.min) / (e.target.max - e.target.min)) * 100;
    e.target.style.setProperty("--value", `${percentage}%`);
    e.target.parentElement.dataset.value = value;
  };

  const handleSubmit = () => {
    // Prepare race settings data
    const raceSettings = {
      raceName,
      raceDuration,
      startDate,
      startTime,
      bettingWindow,
    };

    // Pass the settings up to parent via onNext
    onNext(raceSettings);
  };

  const canProceed = raceName && startDate && startTime;

  const formatRaceSummary = () => {
    if (!startDate || !startTime) return "";

    const date = new Date(startDate + "T" + startTime);
    const formattedDate = date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });

    const endTime = new Date(date.getTime() + bettingWindow.after * 60000);
    const bettingEndTime = endTime.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });

    return (
      <div className="race-summary">
        The race will start on the{" "}
        <span className="highlight">
          {formattedDate} at {formattedTime}
        </span>
        , will <span className="highlight">last for {raceDuration} hours</span>{" "}
        and{" "}
        <span className="highlight">
          bets are accepted until {bettingEndTime}
        </span>
      </div>
    );
  };

  return (
    <div className="race-settings-container">
      <div className="wizard-header">
        <div className="step-indicator">2</div>
        <div>
          <h2 className="wizard-title">Race Settings</h2>
          <p className="wizard-description">
            Set the rules for your race by choosing its duration, start time,
            and betting window.
          </p>
        </div>
      </div>

      <div className="settings-form">
        <div className="form-group">
          <label htmlFor="raceName">Race Name</label>
          <input
            type="text"
            id="raceName"
            className="race-name-input"
            placeholder="Race Name"
            value={raceName}
            onChange={handleRaceNameChange}
          />
        </div>

        <div className="form-group">
          <label className="duration-label">
            <Clock size={16} />
            Race Duration
          </label>
          <div className="duration-slider-container">
            <span>1hr</span>
            <div className="slider-wrapper">
              <div
                className="value-bubble"
                style={{ left: `${((raceDuration - 1) / 23) * 100}%` }}
              >
                {raceDuration}
              </div>
              <input
                type="range"
                min="1"
                max="24"
                value={raceDuration}
                onChange={handleDurationChange}
                className="duration-slider"
                style={{ "--value": `${((raceDuration - 1) / 23) * 100}%` }}
              />
            </div>
            <span>24hrs</span>
          </div>
        </div>

        <div className="form-group">
          <label className="start-time-label">
            <Calendar size={16} />
            Race Start Time
          </label>
          <div className="datetime-inputs">
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              className="date-input"
              min={new Date().toISOString().split("T")[0]}
            />
            <input
              type="time"
              value={startTime}
              onChange={handleStartTimeChange}
              className="time-input"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="betting-window-label">
            <Calendar size={16} />
            Betting Window
          </label>
          <div className="betting-window-sliders">
            <div className="betting-window-label">
              <span>Before Race:</span>
              <div className="slider-wrapper mt-10">
                <div
                  className="value-bubble"
                  style={{
                    left: `${((bettingWindow.before - 5) / 55) * 100}%`,
                  }}
                >
                  {bettingWindow.before}
                </div>
                <input
                  type="range"
                  min="5"
                  max="60"
                  value={bettingWindow.before}
                  onChange={(e) => handleBettingWindowChange("before", e)}
                  className="betting-slider"
                  style={{
                    "--value": `${((bettingWindow.before - 5) / 55) * 100}%`,
                  }}
                />
              </div>
              <span>{bettingWindow.before} mins</span>
            </div>
          </div>
        </div>
      </div>

      {formatRaceSummary()}

      <div className="button-container">
        {currentStep > 1 && (
          <button onClick={onBack} className="back-button" type="button">
            Back
          </button>
        )}
        <button
          onClick={handleSubmit}
          className="continue-button"
          disabled={!canProceed || isLastStep}
          type="button"
        >
          {isLastStep ? "Finish" : "Continue"}
        </button>
      </div>
    </div>
  );
};

StepTwo.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  isLastStep: PropTypes.bool.isRequired,
  initialRaceSettings: PropTypes.shape({
    raceName: PropTypes.string,
    raceDuration: PropTypes.number,
    startDate: PropTypes.string,
    startTime: PropTypes.string,
    bettingWindow: PropTypes.shape({
      before: PropTypes.number,
      after: PropTypes.number,
    }),
  }),
};

StepTwo.defaultProps = {
  initialRaceSettings: {
    raceName: "",
    raceDuration: 1,
    startDate: "",
    startTime: "",
    bettingWindow: { before: 5, after: 5 },
  },
};

export default StepTwo;
